import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { date as fmtDate, num } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { SelectInput } from '@/components/ui/Field';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';

/**
 * Demo data: load months of history on demand, and clear it again.
 *
 * The work is done by the database (migration 0029). The screen only
 * drives it in steps: demo_begin() clears and lays down the fleet, then
 * demo_load_month() once per month, then demo_finish() puts one machine
 * down on a part that is on order. Each step is a separate request, so
 * none of them comes near the API's 8-second limit, and the bar moves.
 *
 * Every demo record's id begins "dededede-". Clearing removes those and
 * nothing else — and keeps any that something real now refers to.
 */

interface DemoStatus {
  enabled?: boolean;
  machines?: number;
  diaries?: number;
  tickets?: number;
  breakdowns?: number;
  blasts?: number;
  services?: number;
  deliveries?: number;
  first_day?: string | null;
  last_day?: string | null;
  adopted?: string[];
}

interface Progress {
  step: number;
  total: number;
  label: string;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: i === 0 ? '1 month' : `${i + 1} months`,
}));

function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
}

export function DemoDataPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [months, setMonths] = useState('8');
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error: err } = await supabase.rpc('demo_status');
    if (err) {
      setError(err.message);
      return;
    }
    setStatus((data ?? {}) as DemoStatus);
  }, []);

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin, refresh]);

  async function load() {
    setError(null);
    const n = Number(months);
    const total = n + 2;
    try {
      setProgress({ step: 0, total, label: 'Clearing old demo data and setting up the fleet' });
      const begin = await supabase.rpc('demo_begin', { p_months: n });
      if (begin.error) throw begin.error;
      const list = ((begin.data as { months?: string[] })?.months ?? []);

      let tickets = 0;
      for (let i = 0; i < list.length; i += 1) {
        setProgress({ step: i + 1, total, label: `Loading ${monthLabel(list[i])}` });
        const res = await supabase.rpc('demo_load_month', { p_month: list[i] });
        if (res.error) throw res.error;
        tickets += Number((res.data as { tickets?: number })?.tickets ?? 0);
      }

      setProgress({ step: total - 1, total, label: 'Putting one machine down, waiting on a part' });
      const finish = await supabase.rpc('demo_finish');
      if (finish.error) throw finish.error;
      setStatus((finish.data ?? {}) as DemoStatus);

      setProgress({ step: total, total, label: 'Done' });
      toast.success(`Loaded ${list.length} months of demo data — ${num(tickets)} weighbridge tickets`);
      void syncNow();
    } catch (err) {
      const message = (err as { message?: string })?.message ?? 'Loading failed';
      setError(`${message} Nothing is lost: load again to start over, or clear what was loaded.`);
      toast.error('Loading demo data stopped part-way');
      void refresh();
    } finally {
      setProgress(null);
    }
  }

  async function clear(): Promise<boolean> {
    setError(null);
    const { data, error: err } = await supabase.rpc('demo_clear');
    if (err) {
      setError(err.message);
      toast.error('Clearing demo data failed');
      return false;
    }
    const removed = Object.entries((data ?? {}) as Record<string, number>)
      .filter(([k]) => k !== 'audit_entries')
      .reduce((sum, [, v]) => sum + Number(v), 0);
    toast.success(`Demo data cleared — ${num(removed)} records removed`);
    await refresh();
    void syncNow();
    return true;
  }

  if (!isAdmin) {
    return (
      <Panel title="Demo data">
        <p className="muted">Only an administrator can load or clear demo data.</p>
      </Panel>
    );
  }

  const busy = progress !== null;
  const hasDemo = (status?.diaries ?? 0) > 0 || (status?.machines ?? 0) > 0;
  const enabled = status?.enabled !== false;

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <Panel title="Demo data now in the system">
        {!status ? (
          <div className="centered"><span className="spinner" /></div>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-3)' }}>
            <div className="grid grid--stats">
              <StatTile label="Machines" value={status.machines ?? 0} />
              <StatTile label="Diaries" value={status.diaries ?? 0} />
              <StatTile label="Weighbridge tickets" value={status.tickets ?? 0} />
              <StatTile label="Breakdowns" value={status.breakdowns ?? 0} />
              <StatTile label="Services" value={status.services ?? 0} />
              <StatTile label="Blasts" value={status.blasts ?? 0} />
              <StatTile label="Concrete deliveries" value={status.deliveries ?? 0} />
            </div>
            <div className="small muted">
              {status.first_day && status.last_day
                ? `Demo diaries run from ${fmtDate(status.first_day)} to ${fmtDate(status.last_day)}.`
                : 'No demo diaries loaded.'}
              {status.adopted && status.adopted.length > 0 && (
                <> {status.adopted.join(', ')} began as a demo machine and has been put to real use; clearing
                  keeps {status.adopted.length === 1 ? 'it' : 'them'} and {status.adopted.length === 1 ? 'its' : 'their'} real
                  records, and removes only the demo history.</>
              )}
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Load demo data">
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <p className="small muted" style={{ margin: 0 }}>
            A fleet of eight — two excavators, four dump trucks, a loader and a crusher — with a
            diary for every machine, every working day, up to today. Every truck load weighed; a
            blast every three weeks; breakdowns that thin out and standby that shrinks, so
            availability and utilisation improve over the period; summer rain; services every
            250 hours; crusher liners worn out and replaced; parts issued and restocked. One truck is
            down this morning, waiting on a hydraulic pump that is on order. The four concrete
            branches get deliveries every working day — mixes, customers, some concrete disposed of
            with its reason — and a monthly target each, a small fleet of mixer trucks with a daily
            check every working day, the odd breakdown, and one Swakopmund truck down this morning.
            The workshop gets four technicians, a closed job card with hours and parts for every
            breakdown and mixer service, and this morning's board: the machines that are down and the
            next two services.
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            Loading replaces any demo data already there. Real records are never touched, and none of it
            appears in the audit trail. Every tablet rebuilds its copy on its next sync.
          </p>

          {!enabled && (
            <div className="issue issue--warn" style={{ display: 'block' }}>
              Loading is switched off (Settings → <strong>Allow loading demo data</strong>). Clearing is still
              available.
            </div>
          )}

          <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ width: 180 }}>
              <SelectInput
                id="demo-months"
                label="History to load"
                value={months}
                onChange={(v) => setMonths(v ?? '8')}
                options={MONTH_OPTIONS}
                disabled={busy || !enabled}
              />
            </div>
            <div className="field">
              <Button onClick={() => void load()} loading={busy} disabled={busy || !enabled}>
                Load {months === '1' ? '1 month' : `${months} months`}
              </Button>
            </div>
          </div>

          {progress && (
            <div className="col" style={{ gap: 'var(--sp-2)' }} aria-live="polite">
              <div className="small">{progress.label}…</div>
              <div className="meter" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total}
                   aria-valuenow={progress.step}>
                <div className="meter__fill"
                     style={{ width: `${Math.round((progress.step / progress.total) * 100)}%`,
                              background: 'var(--accent)' }} />
              </div>
            </div>
          )}

          {error && <div className="issue issue--block" style={{ display: 'block' }}>{error}</div>}
        </div>
      </Panel>

      <Panel title="Clear demo data">
        <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <p className="small muted" style={{ margin: 0, flex: '1 1 320px' }}>
            Removes every demo record and leaves real ones exactly as they are. A demo record that
            something real now refers to — a demo machine put to work, say — is kept. To remove
            everything, including records captured while testing, use <Link to="/admin/reset">Reset system</Link>.
          </p>
          <ConfirmRemove
            label="Clear demo data"
            question="Remove all demo data?"
            disabled={busy || !hasDemo}
            onConfirm={clear}
          />
        </div>
      </Panel>
    </div>
  );
}
